import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTeams } from '../use-teams';
import { useParams, useSearchParams } from 'react-router-dom';
import { useGoBack, useWorkspaceNavigate } from '../../workspace-path';
import {
  REPORT_FILTER_KEYS,
  clientSchema,
  contractSchema,
  lineItemSchema,
  metafieldSchema,
  personSchema,
  projectSchema,
  reportSchema,
  reportSnapshotSchema,
  taskPrioritySchema,
  taskStatusSchema,
  type Client,
  type Contract,
  type LineItem,
  type TaskPriority,
  type TaskStatus,
  type Metafield,
  type Person,
  type Project,
  type Report,
  type ReportDefinition,
  type ReportSnapshot,
} from '@forecastify/shared';
import { Toaster, type Toast } from '@forecastify/ui';
import { useT } from '../../i18n';
import { useLiveInvalidation } from '../../live/use-live';
import { useFirstLoad } from '../use-first-load';
import { REPORT_TOPICS } from '../../live/topics';
import { useSession } from '../../session';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { parseList, usePermissionSet } from '../permissions-hook';
import { ReportsScreen } from './reports-screen';
import { BuilderScreen, asDefinition, type BuilderDraft, type FilterKey } from './builder-screen';
import { useExport } from '../exports/use-export';
import { envelopeOf, messageFor } from './report-errors';
import { FALLBACK_CHART } from '@forecastify/shared';
import { isTemplateId, templateDraft, templateName } from './report-templates';
import { requestAll } from '../read-all';

/**
 * C15's two routes: `/reports` lists, `/reports/new` and `/reports/:id` build.
 *
 * ⚠️ **The list's filters live here and become query parameters**, because the service filters in
 * SQL. A filter applied in the browser would be a second implementation of the same predicate.
 */

let toastCounter = 0;
const nextToastId = (): string => `report-${String((toastCounter += 1))}`;

/* ── the saved list ─────────────────────────────────────────────────────────────────────────── */

export function ReportsRoute({ mine = false }: { readonly mine?: boolean } = {}) {
  const session = useSession();
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();
  const t = useT();
  const permissions = usePermissionSet();

  const [reports, setReports] = useState<readonly Report[]>([]);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('');
  const [dimension, setDimension] = useState('');
  const [view, setView] = useState('all');
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const exporter = useExport();

  /**
   * ⚠️ **The view and the source filter are the SAME server parameter**, and the mockup applies both
   * — its `repListRows` filters by `repSrcFilter` and then by `repSavedView`, so picking
   * *"Óra-riportok"* and then filtering to *"Feladatok"* yields nothing. One parameter cannot
   * disagree with itself; the view wins because it is the coarser control.
   */
  const effectiveSource = view === 'all' ? source : view;

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (search.trim() !== '') params.set('search', search.trim());
    if (effectiveSource !== '') params.set('source', effectiveSource);
    if (dimension !== '') params.set('dimension', dimension);
    const text = params.toString();
    return text === '' ? '' : `?${text}`;
  }, [search, effectiveSource, dimension]);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    const response = await session.request(`/reports${query}`).catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }

    const parsed = parseList(reportSchema, await response.json());
    if (parsed === null) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setFailure(null);
    setReports(parsed);
  }, [session, query]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /* ⚠️ C21 · the same constant `routes.tsx` declares for this screen. A colleague's change
   * refetches this in place — no spinner, no blank, no reload. R-19. */
  useLiveInvalidation(REPORT_TOPICS, reload);

  const toast = (entry: Omit<Toast, 'id'>): void => {
    setToasts((current) => [...current, { ...entry, id: nextToastId() }]);
  };

  return (
    <>
      <ScreenState failure={failure} onRetry={() => void reload()}>
        <ReportsScreen
          mine={mine}
          loading={loading}
          reports={reports}

          search={search}
          source={source}
          dimension={dimension}
          view={view}
          canManage={permissions.has('report.manage')}
          canExport={permissions.has('report.export')}
          onExport={(report) => {
            exporter.open('report', { reportId: report.id });
          }}
          onSearch={setSearch}
          onSource={setSource}
          onDimension={setDimension}
          onView={setView}
          onOpen={(report) => {
            go(`/reports/${report.id}`);
          }}
          onNew={() => {
            go('/reports/new');
          }}
          onTemplate={(templateId) => {
            go(`/reports/new?template=${templateId}`);
          }}
          onDuplicate={(report) => {
            void (async () => {
              const response = await session
                .request(`/reports/${report.id}/duplicate`, {
                  method: 'POST',
                  /* ⚠️ The copy's name is the CLIENT's, because *"(másolat)"* is user-facing text and
                   * rule 7 keeps every one of those on this side of the wire. */
                  body: JSON.stringify({ name: t('report.copyName', { name: report.name }) }),
                })
                .catch(() => null);

              if (response?.ok !== true) {
                toast({ title: t('error.unavailable'), variant: 'error' });
                return;
              }
              await reload();
              toast({
                title: t('report.toast.duplicated'),
                description: report.name,
                variant: 'success',
              });
            })();
          }}
          onDelete={(report) => {
            void (async () => {
              const response = await session
                .request(`/reports/${report.id}?version=${String(report.version)}`, {
                  method: 'DELETE',
                })
                .catch(() => null);

              if (response?.ok !== true) {
                toast({ title: t('error.unavailable'), variant: 'error' });
                return;
              }
              await reload();
              toast({
                title: t('report.toast.deleted'),
                description: report.name,
                variant: 'success',
              });
            })();
          }}
        />
      </ScreenState>

      {exporter.dialog}

      {/* ⚠️ One `Toaster`, both sources — two would stack in the same corner and hide each other. */}
      <Toaster
        toasts={[...toasts, ...exporter.toasts]}
        label={t('report.toasts')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
          exporter.dismiss(id);
        }}
        dismissLabel={t('common.close')}
      />
    </>
  );
}

/* ── the builder ────────────────────────────────────────────────────────────────────────────── */

/**
 * ⚠️ **The mockup's own opening state**: worklogs, by project, this month, no metric yet. Its
 * `newReport()` seeds exactly this, and the empty metric is what puts the *"Kezdd egy mértékkel"*
 * card on screen.
 */
function blankDraft(from: string | null, to: string | null): BuilderDraft {
  return {
    source: 'worklog',
    dimension: 'project',
    dimensionMetafieldId: null,
    metric: null,
    /* ⚠️ The one shape every report can draw — `charts.ts` names it, so the default and the
       fallback cannot drift apart. */
    chart: FALLBACK_CHART,
    periodFrom: from,
    periodTo: to,
    filterProjectId: null,
    filterMemberId: null,
    filterMetafieldId: null,
    filterMetafieldValue: null,
  };
}

/** Which filter rows a loaded definition should show — the mockup's `openReport` does the same. */
function filtersOf(definition: ReportDefinition): FilterKey[] {
  const keys: FilterKey[] = REPORT_FILTER_KEYS.filter(
    (key) => (definition.filters?.[key] ?? []).length > 0,
  );
  if (definition.filterMetafieldId !== null) keys.push('metafield');
  return keys;
}

/**
 * ⚠️ 16 Oct 2026 · **A report saved with the old single project or person filter reopens with it as
 * one value of the new rows.** The rail has no single-choice rows any more, so leaving the old field
 * set would narrow the report by something no row on screen shows.
 */
function upgraded(definition: ReportDefinition): ReportDefinition {
  if (definition.filterProjectId === null && definition.filterMemberId === null) return definition;
  const filters = { ...(definition.filters ?? {}) };
  if (definition.filterProjectId !== null) {
    filters.projects = [...new Set([...(filters.projects ?? []), definition.filterProjectId])];
  }
  if (definition.filterMemberId !== null) {
    filters.members = [...new Set([...(filters.members ?? []), definition.filterMemberId])];
  }
  return { ...definition, filterProjectId: null, filterMemberId: null, filters };
}

export function BuilderRoute() {
  const session = useSession();
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();
  const goBack = useGoBack();
  const params = useParams();
  const t = useT();
  const permissions = usePermissionSet();

  /** `undefined` on `/reports/new`; the route declares both paths. */
  const id = params.id;
  const [search] = useSearchParams();

  /**
   * ⚠️ **Today is read ONCE per mount**, not on every render. `periodOf` compares against it, so a
   * value that changed mid-session would relabel a range without anything having moved.
   */
  const [today] = useState(() => new Date().toISOString().slice(0, 10));

  /**
   * `/reports/new?projectId=<id>` — the projects list's *Riport erre a projektre*.
   *
   * The mockup's own version is `newReport({ agg: 'hours', pid: id }, ['pid'], name + ' — óra')`:
   * an hours report already filtered to that project, with its filter row open. Seeded in the
   * initialisers rather than by an effect, so the builder is never briefly a different report —
   * and so nothing pulls the reader back to this project after they change it.
   */
  const seededProjectId = search.get('projectId');
  /**
   * 17 Oct 2026 · `/reports/new?template=<id>` — the reports list's *Sablonból*: the builder opens
   * already set up as one of the ready-made reports (`report-templates.ts`). Seeded in the
   * initialisers for the reason `projectId` gives.
   */
  const templateId = search.get('template');
  const template =
    id === undefined && isTemplateId(templateId) ? templateDraft(templateId, today) : null;

  const [name, setName] = useState(() =>
    template !== null && isTemplateId(templateId)
      ? templateName(t, templateId)
      : t('report.newName'),
  );
  const [draft, setDraft] = useState<BuilderDraft>(() =>
    template !== null
      ? template.draft
      : {
          ...blankDraft(`${today.slice(0, 8)}01`, today),
          ...(id === undefined && seededProjectId !== null
            ? { metric: 'hours' as const, filters: { projects: [seededProjectId] } }
            : {}),
        },
  );
  const [filters, setFilters] = useState<readonly FilterKey[]>(
    template !== null
      ? template.filters
      : id === undefined && seededProjectId !== null
        ? ['projects']
        : [],
  );
  const [snapshot, setSnapshot] = useState<ReportSnapshot | null>(null);
  /** C60 · the run was refused because the caller may not see money — see the effect below. */
  const [moneyLocked, setMoneyLocked] = useState(false);
  const [ranAt, setRanAt] = useState<string | null>(null);
  const [version, setVersion] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [showTable, setShowTable] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);

  const [projects, setProjects] = useState<readonly Project[]>([]);
  const [people, setPeople] = useState<readonly Person[]>([]);
  const [metafields, setMetafields] = useState<readonly Metafield[]>([]);
  /* 16 Oct 2026 · the list layout's pickers: every field (not only `select`) and the filter lists. */
  const [allMetafields, setAllMetafields] = useState<readonly Metafield[]>([]);
  const [clients, setClients] = useState<readonly Client[]>([]);
  const [contracts, setContracts] = useState<readonly Contract[]>([]);
  const [lineItems, setLineItems] = useState<readonly LineItem[]>([]);
  const [statuses, setStatuses] = useState<readonly TaskStatus[]>([]);
  const [priorities, setPriorities] = useState<readonly TaskPriority[]>([]);
  /* R-60 · the *Csapat* filter's options. */
  const teams = useTeams();
  const [listReloadKey, setListReloadKey] = useState(0);
  const exporter = useExport();

  const toast = (entry: Omit<Toast, 'id'>): void => {
    setToasts((current) => [...current, { ...entry, id: nextToastId() }]);
  };

  /**
   * The pickers.
   *
   * ⚠️ **All three NON-FATAL**, the rule `task-route.tsx` records: a caller without `project.view`
   * should still get the builder rather than a full-page error about a filter they never opened.
   *
   * ⚠️ **Only `select` metafields on TASKS**, because those are the only ones the server will group
   * or filter by. Offering the others would be inviting a `metafield_not_groupable`.
   */
  useEffect(() => {
    void (async () => {
      if (session.workspaceId === null) return;

      const [
        projectList,
        peopleList,
        fieldList,
        clientList,
        contractList,
        itemList,
        statusList,
        priorityList,
      ] = await Promise.all([
        requestAll(session, '/projects').catch(() => null),
        /* 26 Oct 2026 · the API's ceiling, not its default page of 50 — the people filter
           ended at *M* otherwise. */
        requestAll(session, '/people').catch(() => null),
        /* ⚠️ Every entity's fields: the list's column picker offers those its source reaches. */
        session.request('/metafields').catch(() => null),
        requestAll(session, '/clients').catch(() => null),
        requestAll(session, '/contracts').catch(() => null),
        session.request('/line-items').catch(() => null),
        session.request('/task-statuses').catch(() => null),
        session.request('/task-priorities').catch(() => null),
      ]);

      if (projectList?.ok === true) {
        const parsed = parseList(projectSchema, await projectList.json());
        if (parsed !== null) setProjects(parsed);
      }
      if (peopleList?.ok === true) {
        const parsed = parseList(personSchema, await peopleList.json());
        if (parsed !== null) setPeople(parsed);
      }
      if (fieldList?.ok === true) {
        const parsed = parseList(metafieldSchema, await fieldList.json());
        if (parsed !== null) {
          setAllMetafields(parsed);
          /* ⚠️ The summary groups and filters by `select` task fields only — see the service. */
          setMetafields(
            parsed.filter((field) => field.type === 'select' && field.entity === 'task'),
          );
        }
      }
      /* ⚠️ All non-fatal, like the three above: a filter list that fails to load is an empty one. */
      if (clientList?.ok === true) {
        const parsed = parseList(clientSchema, await clientList.json());
        if (parsed !== null) setClients(parsed);
      }
      if (contractList?.ok === true) {
        const parsed = parseList(contractSchema, await contractList.json());
        if (parsed !== null) setContracts(parsed);
      }
      if (itemList?.ok === true) {
        const parsed = parseList(lineItemSchema, await itemList.json());
        if (parsed !== null) setLineItems(parsed);
      }
      if (statusList?.ok === true) {
        const parsed = parseList(taskStatusSchema, await statusList.json());
        if (parsed !== null) setStatuses(parsed);
      }
      if (priorityList?.ok === true) {
        const parsed = parseList(taskPrioritySchema, await priorityList.json());
        if (parsed !== null) setPriorities(parsed);
      }
    })();
  }, [session]);

  /** A saved report, loaded into the builder. */
  useEffect(() => {
    if (id === undefined) return;

    void (async () => {
      if (session.workspaceId === null) {
        setFailure({ kind: 'no-workspace' });
        return;
      }

      const response = await session.request(`/reports/${id}`).catch(() => null);
      if (response === null) {
        setFailure({ kind: 'error', code: 'unavailable' });
        return;
      }
      if (!response.ok) {
        setFailure(await failureOf(response));
        return;
      }

      const parsed = reportSchema.safeParse(await response.json());
      if (!parsed.success) {
        setFailure({ kind: 'error', code: 'validation_failed' });
        return;
      }

      setFailure(null);
      setName(parsed.data.name);
      const loaded = upgraded(parsed.data.definition);
      setDraft({ ...loaded });
      setFilters(filtersOf(loaded));
      /* ⚠️ **The STORED snapshot, not a fresh run** — that is what P-3 means. Refresh is how somebody
       * asks for today's numbers, and `ran_at` beside it is how they know they have not. */
      setSnapshot(parsed.data.snapshot);
      setRanAt(parsed.data.ranAt);
      setVersion(parsed.data.version);
    })();
  }, [session, id]);

  /**
   * The live preview.
   *
   * ⚠️⚠️ **AN UNSAVED BUILDER RUNS; A SAVED ONE SHOWS ITS SNAPSHOT UNTIL REFRESHED.** Re-running a
   * saved report on every keystroke would make `ran_at` a lie the moment somebody looked at it, and
   * would silently replace the numbers they opened the report to see. So the preview only drives the
   * unsaved case and the case where the definition has been edited since it loaded.
   */
  const definition = asDefinition(draft);
  const definitionKey = definition === null ? '' : JSON.stringify(definition);
  const [ranKey, setRanKey] = useState<string | null>(null);

  /* A list and a timeline have no snapshot to lock behind the money pane — their views say so. */
  const isList = definition?.layout === 'list' || definition?.layout === 'timeline';

  useEffect(() => {
    if (definition === null) return;
    /* ⚠️ 16 Oct 2026 · a LIST runs in `ReportListView`, a page at a time — there is no snapshot;
       17 Oct 2026 · and a TIMELINE in `ReportTimelineView`, the same way. */
    if (definition.layout === 'list' || definition.layout === 'timeline') return;
    /* Already showing this exact definition's numbers. */
    if (ranKey === definitionKey) return;
    /* A freshly loaded saved report: its snapshot IS this definition's answer. */
    if (id !== undefined && ranKey === null && snapshot !== null) {
      setRanKey(definitionKey);
      return;
    }

    /**
     * ⚠️⚠️ **A MUTABLE OBJECT, NOT A `let`.** TypeScript narrows a `let` initialised to `true` to the
     * literal `true` — the reassignment lives in a cleanup closure it cannot see running — so
     * `if (!live)` becomes provably dead and lint says so — `hard-won.md` C15-3. A `{ current }`
     * object cannot be narrowed away.
     */
    const alive = { current: true };
    setIsRunning(true);

    void (async () => {
      const response = await session
        .request('/reports/run', { method: 'POST', body: JSON.stringify(definition) })
        .catch(() => null);

      if (!alive.current) return;
      setIsRunning(false);

      if (response === null) {
        setServerError(t('error.unavailable'));
        return;
      }
      if (!response.ok) {
        const { code, detail } = await envelopeOf(response);
        /**
         * C60 · ⚠️⚠️ **THE LOCKED PANE EXISTS FOR A SAVED REPORT, NOT FOR THE PICKER.** The measure
         * menu already disables what the caller may not pick — but a report SAVED while its author
         * held `report.money`, opened after they lost it, arrives here. A generic *"that is not
         * available"* would read as a broken screen; the shield and the sentence tell them to ask
         * their admin, which is the only thing they can actually do.
         */
        setMoneyLocked(code === 'money_reports_not_allowed');
        setServerError(code === 'money_reports_not_allowed' ? null : messageFor(t, code, detail));
        return;
      }
      setMoneyLocked(false);

      const parsed = reportSnapshotSchema.safeParse(await response.json());
      if (!parsed.success) {
        setServerError(t('error.unavailable'));
        return;
      }
      setServerError(null);
      setSnapshot(parsed.data);
      setRanKey(definitionKey);
    })();

    return () => {
      alive.current = false;
    };
  }, [session, t, definitionKey, definition, ranKey, id, snapshot]);

  const patch = (next: Partial<BuilderDraft>): void => {
    setDraft((current) => ({ ...current, ...next }));
  };

  return (
    <>
      <ScreenState failure={failure} onRetry={() => undefined}>
        <BuilderScreen
          name={name}
          draft={draft}
          filters={filters}
          snapshot={snapshot}
          /* C60 · ⚠️ UI gating is UX; the server refuses a money measure either way (rule 6). */
          canMoney={permissions.has('report.money')}
          listOptions={{
            projects,
            people,
            clients,
            contracts,
            lineItems,
            statuses,
            priorities,
            teams,
            metafields: allMetafields,
          }}
          listReloadKey={listReloadKey}
          onExport={
            id === undefined || !permissions.has('report.export')
              ? null
              : () => {
                  exporter.open('report', { reportId: id });
                }
          }
          moneyLocked={moneyLocked && !isList}
          ranAt={ranAt}
          reportId={id ?? null}
          today={today}
          projects={projects}
          people={people}
          metafields={metafields}
          showControls={showControls}
          showTable={showTable}
          canManage={permissions.has('report.manage')}
          serverError={serverError}
          isRunning={isRunning}
          onName={setName}
          onDraft={patch}
          onFilters={setFilters}
          onShowControls={setShowControls}
          onShowTable={setShowTable}
          /* ⚠️ Back to where the report was opened from; the list only with no past (14 Oct). */
          onBack={() => {
            goBack('/reports');
          }}
          onSave={() => {
            void (async () => {
              if (definition === null) return;
              setServerError(null);

              /* ⚠️ Save on an unsaved report CREATES; on a saved one it PATCHes the definition, which
               * re-runs and replaces the snapshot. A rename alone does not re-run — see the service. */
              const response = await session
                .request(id === undefined ? '/reports' : `/reports/${id}`, {
                  method: id === undefined ? 'POST' : 'PATCH',
                  body: JSON.stringify(
                    id === undefined ? { name, definition } : { name, definition, version },
                  ),
                })
                .catch(() => null);

              if (response === null) {
                setServerError(t('error.unavailable'));
                return;
              }
              if (!response.ok) {
                const { code, detail } = await envelopeOf(response);
                setServerError(messageFor(t, code, detail));
                return;
              }

              const parsed = reportSchema.safeParse(await response.json());
              if (!parsed.success) {
                setServerError(t('error.unavailable'));
                return;
              }

              setVersion(parsed.data.version);
              setRanAt(parsed.data.ranAt);
              setSnapshot(parsed.data.snapshot);
              toast({ title: t('report.toast.saved'), description: name, variant: 'success' });
              if (id === undefined) go(`/reports/${parsed.data.id}`);
            })();
          }}
          onRefresh={() => {
            void (async () => {
              if (id === undefined) return;
              setServerError(null);
              const response = await session
                .request(`/reports/${id}/refresh`, { method: 'POST' })
                .catch(() => null);

              if (response?.ok !== true) {
                setServerError(t('error.unavailable'));
                return;
              }
              const parsed = reportSchema.safeParse(await response.json());
              if (!parsed.success) return;

              setSnapshot(parsed.data.snapshot);
              setRanAt(parsed.data.ranAt);
              setRanKey(JSON.stringify(parsed.data.definition));
              /* A list stores nothing; its rows re-read. */
              setListReloadKey((current) => current + 1);
              toast({
                title: t('report.toast.refreshed'),
                ...(parsed.data.snapshot === null
                  ? {}
                  : {
                      description: t('report.rowCount', {
                        count: String(parsed.data.snapshot.rows.length),
                      }),
                    }),
                variant: 'success',
              });
            })();
          }}
          onDuplicate={() => {
            void (async () => {
              if (id === undefined) return;
              const response = await session
                .request(`/reports/${id}/duplicate`, {
                  method: 'POST',
                  body: JSON.stringify({ name: t('report.copyName', { name }) }),
                })
                .catch(() => null);

              if (response?.ok !== true) {
                setServerError(t('error.unavailable'));
                return;
              }
              const parsed = reportSchema.safeParse(await response.json());
              if (parsed.success) go(`/reports/${parsed.data.id}`);
            })();
          }}
          onDelete={() => {
            void (async () => {
              if (id === undefined) return;
              const response = await session
                .request(`/reports/${id}?version=${String(version)}`, { method: 'DELETE' })
                .catch(() => null);

              if (response?.ok !== true) {
                setServerError(t('error.unavailable'));
                return;
              }
              go('/reports');
            })();
          }}
        />
      </ScreenState>

      {exporter.dialog}

      <Toaster
        toasts={[...toasts, ...exporter.toasts]}
        label={t('report.toasts')}
        onDismiss={(entryId) => {
          setToasts((current) => current.filter((entry) => entry.id !== entryId));
          exporter.dismiss(entryId);
        }}
        dismissLabel={t('common.close')}
      />
    </>
  );
}
